/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
// import "./src/index.scss"

// You can delete this file if you're not using it
// import 'fontsource-montserrat/400.css';
// import 'fontsource-montserrat/500.css';
// import 'fontsource-montserrat/600.css';
// import 'fontsource-montserrat/700.css';
// import '@fontsource/dela-gothic-one';
import React from 'react';
import { isbot } from 'isbot';
import { getCookieBannerConfig } from './src/utils/cookieBannerConfig';

// Initialize dataLayer and gtag globally
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];

  // Make gtag globally accessible on window object
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  // // questionable
  // if (localStorage.getItem('silktideCookieChoice_analytics') === null) {
  //   localStorage.setItem('silktideCookieChoice_analytics', 'true');
  // }
  
  // // Set consent defaults based on stored preferences (check localStorage first)
  // window.gtag('consent', 'default', {
  //   analytics_storage: localStorage.getItem('silktideCookieChoice_analytics') === 'true' ? 'granted' : 'denied',
  //   ad_storage: 'denied',
  //   ad_user_data: 'denied',
  //   ad_personalization: 'denied',
  //   functionality_storage: localStorage.getItem('silktideCookieChoice_necessary') === 'true' ? 'granted' : 'denied',
  //   security_storage: localStorage.getItem('silktideCookieChoice_necessary') === 'true' ? 'granted' : 'denied'
  // });
}

// Move SEO-critical meta tags to the top of <head> after Gatsby hydration.
// Gatsby Head API re-inserts data-gatsby-head tags at the end of <head>,
// undoing the SSR ordering from onPreRenderHTML.
function reorderHeadMetaTags() {
  const head = document.head;
  // Find the viewport meta (last browser-required tag) as our insertion anchor
  const viewport = head.querySelector('meta[name="viewport"]');
  if (!viewport) return;

  // Move tags in reverse priority order (each insertAfter pushes previous down)
  const selectors = [
    'link[rel="canonical"]',
    'meta[name="description"]',
    'title',
  ];

  for (const sel of selectors) {
    const el = head.querySelector(sel);
    if (el && el !== viewport.nextElementSibling) {
      viewport.after(el);
    }
  }
}

export const onInitialClientRender = () => {
  reorderHeadMetaTags();
  // initCookieBanner();

  // Initialize GTM after a reasonable delay (allows consent to be set)
  setTimeout(initGTM, 1000);

  function initGTM() {
    if (window.gtmDidInit) {
      return false;
    }
    window.dataLayer = window.dataLayer || [];
    window.gtmDidInit = true;

    const script = document.createElement('script');
    script.async = true;
    script.onload = () => {
      window.dataLayer.push({
        event: 'gtm.js',
        'gtm.start': new Date().getTime(),
        'gtm.uniqueEventId': 0,
      });
    };
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-5BVZGPM';
    document.head.appendChild(script);
  }
};

// Track the current cookie banner language
let currentCookieBannerLang = null;

function getLanguageFromPath() {
  // Check if path starts with /de/ for German, otherwise default to English
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/de')) {
    return 'de';
  }
  return 'en';
}

function initCookieBanner() {
  // Skip for bots and crawlers — they don't need cookie consent
  if (typeof navigator !== 'undefined' && isbot(navigator.userAgent)) {
    return;
  }

  // Skip if already loaded
  if (typeof window !== 'undefined' && window.cookieBannerScriptLoaded) {
    return;
  }

  const lang = getLanguageFromPath();
  const config = getCookieBannerConfig(lang);

  // Mark as loaded to prevent duplicate initialization
  if (typeof window !== 'undefined') {
    window.cookieBannerScriptLoaded = true;
    currentCookieBannerLang = lang;

    const script = document.createElement('script');
    script.src = '/cookie-banner/silktide-consent-manager.js';
    script.async = true;
    script.onload = () => {
      if (typeof window.silktideCookieBannerManager !== 'undefined') {
        window.silktideCookieBannerManager.updateCookieBannerConfig(config);
      }
    };
    document.head.appendChild(script);
  }
}

function updateCookieBannerLocale() {
  const lang = getLanguageFromPath();

  // Only update if language changed
  if (lang !== currentCookieBannerLang) {
    currentCookieBannerLang = lang;
    const config = getCookieBannerConfig(lang);

    if (typeof window.silktideCookieBannerManager !== 'undefined') {
      window.silktideCookieBannerManager.updateCookieBannerConfig(config);
    }
  }
}

let isPopNavigation = false;
let currentPathname = null;

export const onPreRouteUpdate = () => {
  // currentPathname is set by onRouteUpdate and always reflects the page the
  // user is currently on — window.location.pathname is unreliable here because
  // pushState has already fired and points to the new URL.
  if (currentPathname) {
    sessionStorage.setItem(`scroll:${currentPathname}`, String(window.scrollY));
  }
};

export const onRouteUpdate = ({ prevLocation, location }) => {
  currentPathname = location.pathname;
  const wasPopNavigation = isPopNavigation;
  isPopNavigation = false;

  if (location && location.state) {
    window.locations = window.locations || [document.referrer];
    window.locations.push(window.location.href);
    window.previousPath = window.locations[window.locations.length - 2];
  }

  if (!wasPopNavigation) {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }

    // Fix incorrect hash navigation
    if (location.hash) {
      const hash = location.hash.substring(1);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        }, 0);
      });
    }
  } else {
    // Back/forward navigation: restore saved scroll position
    const savedY = sessionStorage.getItem(`scroll:${location.pathname}`);
    if (savedY !== null) {
      const targetY = parseInt(savedY, 10);
      // Disable scroll anchoring so post-render layout shifts (image loads,
      // slider init) don't move the viewport away from our restored position.
      document.documentElement.style.overflowAnchor = 'none';
      requestAnimationFrame(() => {
        window.scrollTo(0, targetY);
        setTimeout(() => {
          document.documentElement.style.overflowAnchor = '';
        }, 600);
      });
    }
  }

  // Update cookie banner locale if language changed
  // updateCookieBannerLocale();

  // wrap inside a timeout to ensure the title has properly been changed
  setTimeout(() => {
    reorderHeadMetaTags();

    // if we don't have prev location - that's a first load. We need to skip
    if (!prevLocation) return;
    if (!window.dataLayer) return;
    let data = window.dataLayer;
    let eventName = `gatsby-route-change`;

    data.push({ event: eventName });
  }, 50);
};

// Returning false disables gatsby-react-router-scroll's session-storage
// restore entirely; we scroll to top ourselves in onRouteUpdate.
export const shouldUpdateScroll = () => false;

export const onClientEntry = async () => {
  if (typeof IntersectionObserver === `undefined`) {
    await import(`intersection-observer`);
  }

  // Disable native scroll restoration so it doesn't override our manual restore
  if (window.history?.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }

  // Gatsby calls focus() on #gatsby-focus-wrapper after every route change for
  // accessibility. Without preventScroll the browser natively scrolls to bring
  // the wrapper into view, overriding our restored position.
  const origFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (options) {
    const opts = this.id === 'gatsby-focus-wrapper'
      ? { ...options, preventScroll: true }
      : options;
    return origFocus.call(this, opts);
  };

  window.addEventListener('popstate', () => {
    isPopNavigation = true;
  });
};

export const wrapPageElement = ({ element }) => {
  return <main>{element}</main>;
};
